import React, { useEffect, useState } from "react";
import {
X, BatteryFull, Sun, Car, Thermometer, Activity, Zap, Clock, Gauge, TrendingUp
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area
} from "recharts";
import ChargerApi from "app/views/charger/ChargerApi";
import electricBus from "./ev_charging.png";

const SolarPanelIcon = ({ size = 26, color = "#2563EB" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 64 64"
    fill="none"
    stroke={color}
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
   
    <polygon points="10,22 54,22 48,6 16,6" />
   
    <line x1="22" y1="6" x2="28" y2="22" />
    <line x1="34" y1="6" x2="38" y2="22" />
    <line x1="16" y1="14" x2="54" y2="14" />

  
    <line x1="32" y1="22" x2="32" y2="44" />
    <line x1="20" y1="44" x2="44" y2="44" />
    <line x1="20" y1="44" x2="18" y2="54" />
    <line x1="44" y1="44" x2="46" y2="54" />
  </svg>
);

const generateBatteryData = () => {
  const data = [];
  for (let i = 0; i < 24; i++) {
    data.push({
      time: `${i.toString().padStart(2, "0")}:00`,
      charge: Math.max(20, Math.min(100, 58 + Math.sin(i * 0.3) * 20 + Math.random() * 10)),
      power: Math.max(-5, Math.min(5, Math.sin(i * 0.4) * 3 + Math.random() * 2)),
    });
  }
  return data;
};

const generateProductionData = () => {
  const data = [];
  for (let i = 0; i < 24; i++) {
    let power = 0;
    if (i >= 6 && i <= 18) {
      power = Math.max(0, Math.sin((i - 6) * Math.PI / 12) * 15 + Math.random() * 2);
    }
    data.push({
      time: `${i.toString().padStart(2, "0")}:00`,
      power: Math.round(power * 10) / 10,
      efficiency: Math.max(90, Math.min(100, 97 + Math.random() * 3)),
    });
  }
  return data;
};

const generateChargingData = () => {
  const data = [];
  for (let i = 0; i < 12; i++) {
    data.push({
      time: `${i * 10}min`,
      power: Math.max(0, 11.2 - i * 0.5 + Math.random() * 2),
      soc: Math.min(100, 42 + i * 4 + Math.random() * 2),
      energy: i * 1.8 + Math.random() * 0.5,
    });
  }
  return data;
};

export default function DetailModal({ isOpen, onClose, type, data }) {
  const [chargerData, setChargerData] = useState({});
  const api = new ChargerApi();

  const batteryData = generateBatteryData();
  const productionData = generateProductionData();
  const chargingData = generateChargingData();

 useEffect(() => {
  const fetchChargerDetails = async () => {
    if (!isOpen || type !== "consumption") return;

    try {
      const { data: apiData } = await api.showChargerStMonitorReport();

      const rawChargerId = data?.name?.split(" (")[0]; 
      const rawConnectorId = data?.connectorId?.replace("C", ""); 

      const found = apiData.find(
        (item) =>
          item.charge_box_id === rawChargerId &&
          String(item.connector_id) === String(rawConnectorId)
      );

      if (found) {
        setChargerData({
          voltage: found.V ? parseFloat(found.V).toFixed(2) : "—",
          current: found.A ? parseFloat(found.A).toFixed(2) : "—",
          power: found.power ? parseFloat(found.power).toFixed(2) : data.power,
          status: found.status || data.status,
          soc: found.Percent || data.vehicleSoC,
        });
      }
    } catch (err) {
      console.error("Failed to fetch charger details:", err);
    }
  };

  fetchChargerDetails();
}, [isOpen, type, data]);


  if (!isOpen || !data) return null;

  const StatCard = ({ icon, label, value }) => (
    <div style={{ background: "#f9fafb", borderRadius: "0.75rem", padding: "0.75rem" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.25rem" }}>
        {icon}
        <span style={{ fontSize: "0.9rem", fontWeight: 500, color: "#374151" }}>{label}</span>
      </div>
      <div style={{ fontSize: "1.1rem", fontWeight: 600, color: "#1f2937" }}>{value}</div>
    </div>
  );

  const ChartSection = ({ title, color, type, data, lines }) => (
    <div style={{ background: "#f9fafb", borderRadius: "0.75rem", padding: "1rem" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
        <TrendingUp color={color} size={18} />
        <span style={{ fontWeight: 500, color: "#374151" }}>{title}</span>
      </div>
      <div style={{ height: "220px" }}>
        <ResponsiveContainer width="100%" height="100%">
          {type === "area" ? (
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="time" stroke="#6b7280" fontSize={12} />
              <YAxis stroke="#6b7280" fontSize={12} />
              <Tooltip />
              {lines.map((l) => (
                <Area key={l.key} type="monotone" dataKey={l.key} stroke={l.color} fill={l.color} fillOpacity={0.2} />
              ))}
            </AreaChart>
          ) : (
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="time" stroke="#6b7280" fontSize={12} />
              <YAxis stroke="#6b7280" fontSize={12} />
              <Tooltip />
              {lines.map((l) => (
                <Line key={l.key} type="monotone" dataKey={l.key} stroke={l.color} strokeWidth={2} dot={false} />
              ))}
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );

  const renderBatteryDetails = (battery) => (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1.5rem" }}>
        <div style={{ background: "#dcfce7", padding: "0.75rem", borderRadius: "0.75rem" }}>
          <BatteryFull style={{ color: "#16a34a" }} size={24} />
        </div>
        <div>
          <h3 style={{ fontSize: "1.25rem", fontWeight: 600 }}>{battery.name}</h3>
          <p style={{ color: "#6b7280" }}>Battery Energy Storage System</p>
        </div>
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: "1rem",
        marginBottom: "1.5rem",
      }}>
        <StatCard icon={<BatteryFull color="#16a34a" />} label="Charge Level" value={`${battery.charge}%`} />
        <StatCard icon={<Gauge color="#3b82f6" />} label="Capacity" value={`${battery.capacity} kWh`} />
        {/* <StatCard icon={<Activity color="#dc2626" />} label="Health" value={`${battery.health}%`} /> */}
        {/* <StatCard icon={<Thermometer color="#f97316" />} label="Temperature" value={`${battery.temperature}°C`} /> */}
        <StatCard icon={<Zap color="#a855f7" />} label="Charge Rate" value={`${battery.chargeRate} kW`} />
        <StatCard icon={<Zap color="#a855f7" />} label="Discharge Rate" value={`${battery.dischargeRate} kW`} />
        {/* <StatCard icon={<Clock color="#4f46e5" />} label="Cycles" value={battery.cycles} /> */}
      </div>

      {/* <ChartSection
        title="24-Hour Performance"
        color="#10b981"
        data={batteryData}
        lines={[
          { key: "charge", color: "#10b981" },
          { key: "power", color: "#3b82f6" },
        ]}
      /> */}
    </>
  );

  const renderInverterDetails = (inverter) => (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1.5rem" }}>
        <div style={{ background: "#dbeafe", padding: "0.75rem", borderRadius: "0.75rem" }}>
          <SolarPanelIcon style={{ color: "#2563eb" }} size={24} />
        </div>
        <div>
          <h3 style={{ fontSize: "1.25rem", fontWeight: 600 }}>{inverter.name}</h3>
          <p style={{ color: "#6b7280" }}>Solar Photovoltaic Inverter</p>
        </div>
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: "1rem",
        marginBottom: "1.5rem",
      }}>
        <StatCard icon={<Zap color="#3b82f6" />} label="Output" value={`${inverter.power} kW`} />
        {/* <StatCard icon={<Activity color="#16a34a" />} label="Efficiency" value={`${inverter.efficiency}%`} /> */}
        {/* <StatCard icon={<Thermometer color="#f97316" />} label="Temperature" value={`${inverter.temperature}°C`} /> */}
        <StatCard icon={<Gauge color="#9333ea" />} label="Status" value={inverter.status} />
        <StatCard icon={<Zap color="#4f46e5" />} label="Voltage" value={`${inverter.voltage} V`} />
        <StatCard icon={<Activity color="#06b6d4" />} label="Current" value={`${inverter.current} A`} />
      </div>

      {/* <ChartSection
        title="24-Hour Production"
        color="#3b82f6"
        type="area"
        data={productionData}
        lines={[{ key: "power", color: "#3b82f6" }]}
      /> */}
    </>
  );

  const renderConsumptionDetails = (item) => (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1.5rem" }}>
        <div style={{ background: "#fef9c3", padding: "0.75rem", borderRadius: "0.75rem" }}>
           <img
              src={electricBus}
              width={24}
              alt="electric bus"
              style={{ filter: "invert(51%) sepia(94%) saturate(1200%) hue-rotate(2deg) brightness(95%) contrast(90%)" }}
            />
        </div>
        <div>
          <h3 style={{ fontSize: "1.25rem", fontWeight: 600 }}>{item.name}</h3>
          <p style={{ color: "#6b7280" }}>Electric Vehicle Charging Station</p>
          {item.vehicleId && <p style={{ fontSize: "0.9rem", color: "#2563eb" }}>Vehicle: {item.vehicleId}</p>}
        </div>
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: "1rem",
        marginBottom: "1.5rem",
      }}>
        <StatCard icon={<Zap color="#f59e0b" />} label="Current Power" value={`${item.power} kW`} />
        <StatCard icon={<Activity color="#16a34a" />} label="Status" value={item.status} />
        {item.vehicleSoC !== undefined && (
          <StatCard icon={<BatteryFull color="#2563eb" />} label="Vehicle SoC" value={`${item.vehicleSoC}%`} />
        )}
        {/* <StatCard icon={<Clock color="#4f46e5" />} label="Connected Time" value={item.connectedTime} /> */}
        <StatCard icon={<Gauge color="#9333ea" />} label="Voltage" value={`${chargerData.voltage || "—"} V`} />
        <StatCard icon={<Activity color="#06b6d4" />} label="Current" value={`${chargerData.current || "—"} A`} />
        {/* <StatCard icon={<Battery color="#6366f1" />} label="Session Energy" value={`${item.sessionEnergy} kWh`} /> */}
      </div>

      {item.status === "charging" && (
        <ChartSection
          title="Charging Session Progress"
          color="#f59e0b"
          data={chargingData}
          lines={[
            { key: "power", color: "#f59e0b" },
            { key: "soc", color: "#3b82f6" },
          ]}
        />
      )}
    </>
  );

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
        display: "flex", justifyContent: "center", alignItems: "center",
        zIndex: 1000, padding: "1rem",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "white", borderRadius: "1rem",
          width: "100%", maxWidth: "900px", maxHeight: "80vh",
          display: "flex", flexDirection: "column",
          boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            flexShrink: 0, padding: "1rem 1.5rem", borderBottom: "1px solid #e5e7eb",
            display: "flex", justifyContent: "space-between", alignItems: "center",
            background: "white", borderTopLeftRadius: "1rem", borderTopRightRadius: "1rem",
          }}
        >
          <h2 style={{ fontSize: "1.25rem", fontWeight: 600 }}>Device Details</h2>
          <button
            onClick={onClose}
            style={{ border: "none", background: "none", cursor: "pointer", padding: "0.3rem" }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ overflowY: "auto", padding: "1.5rem", flex: 1 }}>
          {type === "battery" && renderBatteryDetails(data)}
          {type === "inverter" && renderInverterDetails(data)}
          {type === "consumption" && renderConsumptionDetails(data)}
        </div>
      </div>
    </div>
  );
}
